import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BookMarket, bookCrossover, ParlayGap } from '../utils/bookMode';
import { toAmericanOdds } from './OddsToggle';

const ratioText = (r: number) => `×${r.toFixed(2)}`;

// Bars are on a log scale around ×1 (no gap): half the track is a factor of 2 either way.
const BAR_HALF = Math.log(2);

/** A diverging bar: red to the left of center when the factor costs us, green to the right when it helps. */
const RatioBar: React.FC<{ ratio: number }> = ({ ratio }) => {
  const share = Math.min(Math.abs(Math.log(ratio)) / BAR_HALF, 1) * 50;
  const good = ratio >= 1;
  return (
    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: 'action.hover', my: 0.5 }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: good ? '50%' : `${50 - share}%`,
          width: `${share}%`,
          borderRadius: 4,
          bgcolor: good ? 'success.main' : 'error.main',
          opacity: 0.8,
        }}
      />
      <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: 'calc(50% - 1px)', width: 2, bgcolor: 'text.secondary' }} />
    </Box>
  );
};

const FactorRow: React.FC<{ label: React.ReactNode; detail: string; ratio: number; tip: string }> = ({
  label,
  detail,
  ratio,
  tip,
}) => (
  <Box sx={{ mt: 0.75 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
      <Tooltip title={tip}>
        <Typography variant="body2">
          {label}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            ({detail})
          </Typography>
        </Typography>
      </Tooltip>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: ratio >= 1 ? 'success.main' : 'error.main' }}
      >
        {ratioText(ratio)}
      </Typography>
    </Stack>
    <RatioBar ratio={ratio} />
  </Box>
);

const CROSSOVER_LABELS: [BookMarket, string][] = [
  ['win', 'win'],
  ['top3', 'top 3'],
  ['top5', 'top 5'],
  ['top10', 'top 10'],
];

interface ParlayGapBoxProps {
  gap: ParlayGap;
  /** Short labels of the legs, in the same order as gap.legs. */
  legLabels: string[];
  /** The book's implied probability for the parlay (from the odds typed in). */
  implied: number;
  joint: number;
}

/**
 * Where a parlay's gap from the book's price comes from: the legs at typical book prices (multiplied, so small per-leg
 * gaps compound) and this book's price against the typical one.
 */
export const ParlayGapBox: React.FC<ParlayGapBoxProps> = ({ gap, legLabels, implied, joint }) => {
  const bookRatio = gap.typical / implied;
  const total = joint / implied;
  const n = gap.legs.length;
  const above = gap.legs.filter((l) => l.ratio < 1).length;
  const worst = gap.legs.reduce((w, l, i) => (l.ratio < gap.legs[w].ratio ? i : w), 0);

  let note: string;
  if (total >= 1) {
    note =
      gap.legsRatio >= 1
        ? 'The legs are priced at or below typical book prices, so the parlay keeps that edge.'
        : "The legs cost you at typical book prices, but this book's price is long enough to make up for it.";
  } else if (gap.legsRatio < 1) {
    const handicap = `${Math.round((1 - gap.legsRatio) * 100)}%`;
    note =
      `At typical book prices the legs cost you, and multiplied over ${n} leg${n === 1 ? '' : 's'} that's a ` +
      `${handicap} handicap. ` +
      (bookRatio > 1
        ? "This book's price is longer than usual, but not enough to make up for the legs."
        : "This book's price is no better than usual either.");
  } else {
    note = "The legs are fairly priced at typical book prices, but this book's price is shorter than usual.";
  }

  const crossovers = CROSSOVER_LABELS.flatMap(([m, label]) => {
    const p = bookCrossover(m);
    return p === null ? [] : [`${label} at ${Math.round(p * 100)}%+`];
  });

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: (t) => alpha(t.palette.primary.main, 0.45),
        bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Where the gap comes from
      </Typography>
      <FactorRow
        label="Leg prices"
        detail={above === n ? `all ${n} priced above ours` : `${above} of ${n} priced above ours`}
        ratio={gap.legsRatio}
        tip="Our probability for each leg divided by what a book would typically post for it, multiplied over the legs."
      />
      <FactorRow
        label="This book vs typical"
        detail={`${toAmericanOdds(implied)} vs ${toAmericanOdds(gap.typical)}`}
        ratio={bookRatio}
        tip="How this book's price compares with what books typically post for this parlay (each leg's typical price multiplied, times how much more or less often the legs hit together in our simulation)."
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Our chance vs this price
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: total >= 1 ? 'success.main' : 'error.main' }}
        >
          {ratioText(total)}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5 }}>
        {note}
      </Typography>
      {(gap.legs[worst].ratio < 1 || crossovers.length > 0) && (
        <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5, color: 'primary.main' }}>
          {gap.legs[worst].ratio < 1 &&
            `Biggest drag: ${legLabels[worst]} (${ratioText(gap.legs[worst].ratio)}). `}
          {crossovers.length > 0 && `Legs books usually price at or below ours: ${crossovers.join(', ')}.`}
        </Typography>
      )}
    </Box>
  );
};
